import { useState, useEffect, useCallback } from 'react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: string;  // content_writer, cppages, cpcustom, briefcontent, servicearea
  contentId: number;
  contentTitle: string;
}

interface ShareData {
  id: number;
  share_token: string;
  content_type: string;
  content_id: number;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  invitations?: { email: string; status: string; created_at: string }[];
}

function getToken(): string {
  return localStorage.getItem('auth_token') || localStorage.getItem('auth_token') || '';
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
}

export default function ShareModal({ isOpen, onClose, contentType, contentId, contentTitle }: ShareModalProps) {
  const [share, setShare] = useState<ShareData | null>(null);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const shareUrl = share ? `${window.location.origin}/share/${share.share_token}` : '';

  const createOrGetShare = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/shares/create', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ content_type: contentType, content_id: contentId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || 'Failed to create share link');
      }
      const data = await res.json();
      setShare(data);

      // Fetch full share details with invitations
      const myShares = await fetch('/api/shares/my-shares', { headers: authHeaders() });
      if (myShares.ok) {
        const shareList = await myShares.json();
        const full = (shareList.shares || []).find((s: ShareData) => s.id === data.id);
        if (full) setShare(full);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [contentType, contentId]);

  useEffect(() => {
    if (isOpen) {
      createOrGetShare();
    } else {
      setShare(null);
      setInviteEmail('');
      setCopied(false);
      setError('');
    }
  }, [isOpen, createOrGetShare]);

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !share) return;
    setInviting(true);
    setError('');
    try {
      const res = await fetch(`/api/shares/${share.id}/invite`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      if (!res.ok) throw new Error('Failed to send invitation');
      const data = await res.json();
      if (data.already_invited) {
        setError('This email has already been invited');
      } else {
        // Refresh share data
        setShare(prev => prev ? {
          ...prev,
          invitations: [...(prev.invitations || []), { email: inviteEmail.trim(), status: 'pending', created_at: new Date().toISOString() }],
        } : null);
        setInviteEmail('');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const removeInvite = async (email: string) => {
    if (!share) return;
    try {
      await fetch(`/api/shares/${share.id}/invite/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      setShare(prev => prev ? {
        ...prev,
        invitations: (prev.invitations || []).filter(i => i.email !== email),
      } : null);
    } catch { /* ignore */ }
  };

  const revokeShare = async () => {
    if (!share || !confirm('Revoke this share link? Anyone with the link will lose access.')) return;
    try {
      await fetch(`/api/shares/${share.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      setShare(null);
      onClose();
    } catch { /* ignore */ }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Share Content</h2>
            <p className="text-sm text-gray-500 truncate max-w-[350px]">{contentTitle}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Generating share link...
            </div>
          ) : share ? (
            <>
              {/* Share Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Share Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-600 font-mono"
                  />
                  <button
                    onClick={copyLink}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      copied
                        ? 'bg-green-100 text-green-700 border border-green-300'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Anyone with this link and an account can view this content. The page is noindexed.
                </p>
              </div>

              {/* Invite by Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Invite by Email</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleInvite()}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={handleInvite}
                    disabled={inviting || !inviteEmail.trim()}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {inviting ? 'Sending...' : 'Invite'}
                  </button>
                </div>
              </div>

              {/* Invited People */}
              {share.invitations && share.invitations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Invited ({share.invitations.length})
                  </label>
                  <div className="space-y-1.5">
                    {share.invitations.map(inv => (
                      <div key={inv.email} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">{inv.email}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            inv.status === 'viewed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {inv.status === 'viewed' ? 'Viewed' : 'Pending'}
                          </span>
                        </div>
                        <button
                          onClick={() => removeInvite(inv.email)}
                          className="text-gray-400 hover:text-red-500 p-0.5"
                          title="Remove"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
              )}

              {/* Revoke */}
              <div className="pt-2 border-t border-gray-100">
                <button
                  onClick={revokeShare}
                  className="text-sm text-red-500 hover:text-red-700 font-medium"
                >
                  Revoke share link
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              {error ? (
                <p className="text-red-600">{error}</p>
              ) : (
                <p className="text-gray-500">Share link was revoked.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
